/* eslint-disable */
//@ts-nocheck
//TODO: fix types
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { FC, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Flex, LoadingOverlay, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';
import { BookContext } from '@/contexts/BookContext/BookContextProvider';

import { BookingMeetingRoomFields } from '@/entities/Booking/components/BookingMeetingRoomFields.tsx';
import { BookingTypeSlider } from '@/entities/BookingTypeSlider/BookingTypeSlider';
import { CarPassSelector } from '@/entities/CarPassSelector/CarPassSelector';
import { GuestFields } from '@/entities/GuestFields/GuestFields';
import { PersonSelector } from '@/entities/PersonSelector/PersonSelector';
import { UserSearch } from '@/entities/User';

import { BookingRadar } from '@/features/BookingRadar';
import { LocationSelectorSheet } from '@/features/LocationSelectorSheet/LocationSelectorSheet';
import { PlaceMap } from '@/features/PlaceMap';
import { PlaceSelectorSheet } from '@/features/PlaceSelectorSheet';

import { bookingApi } from '@/shared/api/booking';
import { bookingObjectsApi } from '@/shared/api/bookingObjects';
import { TODAY } from '@/shared/constant/app';
import { getRequestErrorText } from '@/shared/helpers/requestError';
import { getTaxiPoints } from '@/shared/helpers/utils.ts';
import { normalizeWorkTime } from '@/shared/helpers/workTime.ts';
import { useGetBookingTypeData } from '@/shared/hooks/useGetBookingTypeData';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import useAppStore from '@/shared/store/app.store';
import useBookingStore from '@/shared/store/booking.store';
import { useSettingsStore } from '@/shared/store/settings.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { IRequestError } from '@/shared/types/app.types';
import { BookingType, IBookingObjects } from '@/shared/types/bookingObjects.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { CopyButton } from '@/shared/uikit/components/button/CopyButton';
import { MapButton } from '@/shared/uikit/components/button/mapButton';
import { SwitchSelector } from '@/shared/uikit/components/input';
import { LabelInput } from '@/shared/uikit/components/input';
import { CarInput } from '@/shared/uikit/components/input/CarInput/CarInput';
import { DateTimePicker } from '@/shared/uikit/components/picker/DateTimePicker/DateTimePicker';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { getNearestDate } from '@/utils/date';

import { TMeetingRoomForm, useMeetingRoomForm } from './useMeetingRoomForm';
import { TParkingPlaceForm, useParkingPlaceForm } from './useParkingPlaceForm';
import { useRadarForm } from './useRadarForm';
import { TWorkPlaceForm, useWorkPlaceForm } from './useWorkPlaceForm';

type NewBookingProps = {
	open?: boolean;
	close?: () => void;
};

type NewBookingModel = {
	initTime: Date | null;
	location: {
		label: string;
		id: number;
	} | null;
};

interface Forms {
	work_place: UseFormReturnType<TWorkPlaceForm>;
	parking_place: UseFormReturnType<TParkingPlaceForm>;
	meeting_room: UseFormReturnType<TMeetingRoomForm>;
}

export const NewBooking: FC<NewBookingProps> = ({ open, close }) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const isMobile = useAppStore((state) => state.isMobile);
	const { userRate, admin, moderator } = useContext(AppContext);
	const carPasses = useUserStore((state) => state.carPasses);
	const targetPersonId = useUserStore((state) => state.targetPersonId);
	const date = useAppStore((state) => state.date);
	const location = useSettingsStore((state) => state.location);
	const currentLocation = useSettingsStore((state) => state.currentLocation);
	const workTime = normalizeWorkTime(useSettingsStore((state) => state.workTime));

	const {
		bookingType: type,
		bookingPlace,
		bookingDate,
		bookingLocation,
		bookingLocationPath,
		setBookingPlace,
		setBookingType,
		setBookingLocation,
		setBookingDate,
		setBookingLocationPath,
	} = useBookingStore();

	const { currentBookingLoad } = useContext(BookContext);
	const [isRadar, setIsRadar] = useState(false);
	const [isFastBooking, setIsFastBooking] = useState(false);
	const [isGuest, setIsGuest] = useState(false);
	const [isExternalMeeting, setIsExternalMeeting] = useState(false);
	const [isServiceRequired, setIsServiceRequired] = useState(false);
	const workPlaceForm = useWorkPlaceForm();
	const { form: parkingPlaceForm, isTaxi, setIsTaxi, setIsGuest: setGuest } = useParkingPlaceForm();
	const meetingRoomForm = useMeetingRoomForm();

	const radarForm = useRadarForm({ isPassRequired: type === 'parking_place' });

	const [model, setModel] = useState<NewBookingModel>({
		initTime: dayjs(date[0]).isToday() ? getNearestDate(TODAY).toDate() : dayjs(date[1]).toDate(),
		location: {
			label: '',
			id: bookingLocation?.id ?? currentLocation?.id,
		},
	});

	const [openedLocationSelector, { close: closeLocationSelector, open: openLocationSelector }] =
		useDisclosure(false);

	const [openedPlaceSelector, { close: closePlaceSelector, open: openPlaceSelector }] =
		useDisclosure(false);

	const currentForm = () => {
		switch (type) {
			case 'work_place':
				return workPlaceForm;

			case 'parking_place':
				return parkingPlaceForm;

			case 'meeting_room':
				return meetingRoomForm;

			default:
				return workPlaceForm;
		}
	};

	const bookingType = useGetBookingTypeData({ bookingType: currentForm().values.kind });

	const { data: availableObjects, isLoading: isAvailableObjectsLoading } = useQuery({
		queryKey: [
			'availableObjects',
			bookingLocation,
			currentLocation,
			currentForm().values.date,
			currentForm().values.kind,
			targetPersonId,
		],
		queryFn: () =>
			bookingObjectsApi.getObjectsForBooking(
				String(bookingLocation?.id ?? currentLocation?.id),
				[currentForm().values.date[0] ?? date[0], currentForm().values.date[1] ?? date[1]],
				targetPersonId,
			),
		enabled: Boolean(targetPersonId && (bookingLocation?.id ?? currentLocation?.id)),
	});

	const { data: communalPlaces } = useQuery({
		queryKey: ['communalPlaces', bookingLocation?.id],
		queryFn: () => bookingObjectsApi.getCommunalPlaces(bookingLocation?.id ?? currentLocation?.id),
	});

	const { mutate: createNewBooking, isPending } = useMutation({
		mutationFn: isFastBooking ? bookingApi.createFastNewBooking : bookingApi.createNewBooking,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['bookings'] });
			useShowNotifaction({ text: 'Бронирование создано', type: 'success' });
			currentForm().reset();
			setBookingPlace(null);
			if (isMobile) {
				close?.();
			} else {
				navigate('/', { replace: true });
			}
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: getRequestErrorText(error, 'Не удалось создать бронирование'),
				type: 'error',
			});
		},
	});

	const { mutate: createMultipleBooking, isPending: isCreateMultipleBooking } = useMutation({
		mutationFn: bookingApi.createMultipleBooking,
		onSuccess: ({ data }) => {
			data.forEach((item) => {
				if (item.result.status_code) {
					useShowNotifaction({ text: item.result.message, type: 'error' });
				}
			});

			void queryClient.invalidateQueries({ queryKey: ['bookings'] });
			useShowNotifaction({ text: 'Бронирование создано', type: 'success' });
			setBookingPlace(null);
			currentForm().reset();

			if (isMobile) {
				close?.();
			} else {
				navigate('/', { replace: true });
			}
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: getRequestErrorText(error, 'Не удалось создать бронирование'),
				type: 'error',
			});
		},
	});

	const { mutate: createBookingRadar, isPending: isPendingCreateBookingRadar } = useMutation({
		mutationFn: bookingApi.createBookingRadar,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ['activeRadars'] });
			useShowNotifaction({ text: 'Радар создан', type: 'success' });
			setBookingPlace(null);
			radarForm.reset();

			if (isMobile) {
				close?.();
			} else {
				navigate('/', { replace: true });
			}
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: getRequestErrorText(error, 'Не удалось создать радар'),
				type: 'error',
			});
		},
	});

	const { mutate: createExternalNewBooking, isPending: isCreatingExternalBooking } = useMutation({
		mutationFn: bookingApi.createExternalBooking,
		onSuccess: (response, variables, context) => {
			void queryClient.invalidateQueries({ queryKey: ['bookings'] });
			useShowNotifaction({
				text: 'Бронирование создано',
				type: 'success',
				autoClose: false,
				widget: (
					<CopyButton value={`${window.location.origin}/booking/invite/${response?.data?.id}`} />
				),
			});
			currentForm().reset();
			setBookingPlace(null);
			if (isMobile) {
				close?.();
			} else {
				navigate('/', { replace: true });
			}
		},
		onError: (error: AxiosError<IRequestError>) => {
			useShowNotifaction({
				text: getRequestErrorText(error, 'Не удалось создать бронирование'),
				type: 'error',
			});
		},
	});

	const taxiPoints = getTaxiPoints(communalPlaces);

	const handleResetStates = () => {
		handleSetFastBooking(false);
		handleSetIsTaxi(false);
		handleSetIsGuest(false);
		handleSetIsExternalMeeting(false);
		handleSetIsServiceRequired(false);
		currentForm().setFieldValue('booking_object_id', null);
		radarForm.setFieldValue('booking_object_id', null);
	};

	const handleChangeBookingType = (bookingType: BookingType) => {
		// currentForm().reset();
		handleResetStates();
		setBookingPlace(null);
		setBookingLocation(currentLocation);
		setBookingType(bookingType);
	};

	const handleChangePlace = (place: IBookingObjects) => {
		handleResetStates();
		currentForm().setFieldValue('booking_object_id', place?.id);
		radarForm.setFieldValue('booking_object_id', place?.id);
		setBookingPlace(place);
	};

	// Время пересчитываем только при пересечении границы «сегодня»/другой день,
	// чтобы не сбрасывать выбранное пользователем время между будущими датами.
	const alignTimeWithDate = (next: TDateRange, prev: TDateRange): TDateRange => {
		const [start, end] = next;
		if (!start) return next;

		const nextStartToday = dayjs(start).isToday();
		if (nextStartToday === dayjs(prev[0]).isToday()) return next;

		const alignedStart = nextStartToday
			? getNearestDate(dayjs())
			: dayjs(start).hour(dayjs(workTime[0]).hour()).minute(dayjs(workTime[0]).minute()).second(0);

		const alignedEnd = dayjs(end ?? start)
			.hour(dayjs(workTime[1]).hour())
			.minute(dayjs(workTime[1]).minute())
			.second(0);

		return [alignedStart.toDate(), alignedEnd.toDate()];
	};

	const handleChangeDate = (next: TDateRange) => {
		const prev = currentForm().values.date;
		const isDayChanged =
			!dayjs(next[0]).isSame(prev[0], 'day') || !dayjs(next[1]).isSame(prev[1], 'day');

		currentForm().setFieldValue('date', isDayChanged ? alignTimeWithDate(next, prev) : next);
	};

	const handleCreateNewBooking = () => {
		if (currentForm().isValid()) {
			// const { date, pass_id, is_guest, car_number, ...rest } = form.getTransformedValues();

			if (currentForm().values.kind === 'work_place') {
				const { date, is_guest, guests, ...rest } = currentForm().values;
				const guestPayload = isGuest ? { is_guest, guests } : {};

				if (!dayjs(date[1]).isSame(date[0], 'day')) {
					return createMultipleBooking({
						...rest,
						...guestPayload,
						start: date[0],
						end: date[1],
					});
				}
				return createNewBooking({
					...rest,
					...guestPayload,
					start: date[0],
					end: date[1],
				});
			}

			if (currentForm().values.kind === 'parking_place') {
				const { date, pass_id, is_guest, car_number, guests, ...rest } = (
					currentForm() as Forms['parking_place']
				).getTransformedValues();

				if (!dayjs(date[1]).isSame(date[0], 'day')) {
					return createMultipleBooking({
						...rest,
						start: date[0],
						end: date[1],
						car_number,
					});
				}

				if (isTaxi) {
					return createNewBooking({
						...rest,
						start: date[0],
						end: date[1],
						car_number,
					});
				}

				if (isGuest) {
					return createNewBooking({
						...rest,
						start: date[0],
						end: date[1],
						is_guest,
						car_number,
						guests,
					});
				}
				return createNewBooking({
					...rest,
					start: date[0],
					end: date[1],
					pass_id,
				});
			}

			if (currentForm().values.kind === 'meeting_room') {
				const { date, service_required, car_number, company, isNeedPass, guests, ...rest } = (
					currentForm() as Forms['meeting_room']
				).getTransformedValues();

				if (isExternalMeeting || guests.length) {
					if (isServiceRequired) {
						createExternalNewBooking({
							...rest,
							start: date[0],
							end: date[1],
							service_required,
							car_number,
							company,
						});
					} else {
						createExternalNewBooking({
							...rest,
							start: date[0],
							end: date[1],
							car_number,
							company,
							guests,
						});
					}
				} else {
					createNewBooking({
						...rest,
						start: date[0],
						end: date[1],
						// end: new Date(new Date(date[1]).setMinutes(date[1].getMinutes() - 1)),
					});
				}
			}

			// createNewBooking({
			// 	...rest,
			// 	start: date[0],
			// 	end: date[1],
			// });
		}
	};

	const handleCreateBookingRadar = () => {
		if (radarForm.isValid()) {
			const { date, ...rest } = radarForm.values;
			if (!dayjs(currentForm().values.date[1]).isSame(currentForm().values.date[0], 'day')) {
				const start = currentForm().values.date[0];
				const end = dayjs(currentForm().values.date[0])
					.set('hour', currentForm().values.date[1]?.getHours())
					.set('minute', currentForm().values.date[1]?.getMinutes())
					.set('second', currentForm().values.date[1]?.getSeconds())
					.toDate();
				const periodEnd = currentForm().values.date[1];

				createBookingRadar({
					...rest,
					kind: 'periodic',
					period_start: start,
					period_end: periodEnd,
					start: start,
					end: end,
				});
			} else {
				createBookingRadar({
					...rest,
					kind: 'default',
					start: currentForm().values.date[0],
					end: currentForm().values.date[1],
				});
			}
		}
	};

	const handleSetIsTaxi = (isTaxi: boolean) => {
		if (isTaxi) {
			const newTime = [
				currentForm().values.date[0],
				dayjs(currentForm().values.date[0]).add(30, 'minute').toDate(),
			];
			currentForm().setFieldValue('booking_object_id', communalPlaces[0]?.id);
			currentForm().setFieldValue('car_number', '');
			currentForm().setFieldValue('date', newTime);
		} else {
			currentForm().setFieldValue('booking_object_id', null);
		}

		setIsTaxi(isTaxi);
	};

	const handleSetFastBooking = (isFast: boolean) => {
		if (isFast) {
			const rootLocation =
				currentForm().values?.kind === 'parking_place'
					? location?.parking[0]?.id
					: currentLocation?.id;

			currentForm().setFieldValue('booking_object_id', bookingLocation.id ?? rootLocation);
			radarForm.setValues({
				...radarForm.values,
				booking_object_id: bookingLocation.id ?? rootLocation,
				object_kind: type,
			});
		} else {
			// currentForm().setFieldValue('booking_object_id', bookingPlace?.id ?? null);
			currentForm().setFieldValue('booking_object_id', null);
			radarForm.setValues({
				...radarForm.values,
				booking_object_id: bookingPlace?.id ?? null,
			});
		}
		setBookingPlace(null);
		setIsFastBooking(isFast);
	};

	const handleSetIsGuest = (isGuest: boolean) => {
		const { ...rest } = (currentForm() as Forms['parking_place']).values;
		if (isGuest) {
			if (currentForm().values.kind === 'parking_place') {
				currentForm().setValues({
					...rest,
					// ...(isFastBooking
					// 	? { booking_object_id: location?.parking[0]?.id }
					// 	: { booking_object_id: bookingPlace?.id ?? currentLocation?.id }),
					is_guest: true,
					guests: [{ full_name: '', email: '', phone: '', renter_id: null }],
				});
			}

			if (currentForm().values.kind === 'work_place') {
				currentForm().setValues({
					...rest,
					is_guest: true,
					guests: [{ full_name: '', email: '', phone: '', renter_id: null }],
				});
			}

			radarForm.setValues({
				...radarForm.values,
				booking_object_id: currentLocation?.id,
				object_kind: type,
				kind: 'fast',
			});
		} else {
			currentForm().setValues({
				...rest,
				// booking_object_id: bookingPlace?.id,
				guests: [],
				is_guest: false,
			});

			radarForm.setValues({
				...radarForm.values,
				// booking_object_id: bookingPlace?.id,
				kind: 'default',
			});
		}

		setIsGuest(isGuest);
		setGuest(isGuest);
	};

	const handleSetIsExternalMeeting = (isExternalMeeting: boolean) => {
		setIsFastBooking(false);
		setIsExternalMeeting(isExternalMeeting);
	};

	const handleSetIsServiceRequired = (isServiceRequired: boolean) => {
		const { service_required, ...rest } = currentForm().values;

		if (isServiceRequired) {
			currentForm().setFieldValue('service_required', isServiceRequired);
		} else {
			currentForm().setValues({ ...rest });
		}

		setIsServiceRequired(isServiceRequired);
	};

	const handleSetIsShowCarNumberField = (value: boolean) => {
		(currentForm() as TMeetingRoomForm).setFieldValue('show_auto', value);
	};

	const actions = () => {
		if (currentBookingLoad) {
			switch (true) {
				case currentBookingLoad >= 100: {
					if (isRadar && radarForm.isValid()) {
						return [
							{
								children: 'Начать поиск',
								loading: isPendingCreateBookingRadar,
								onClick: handleCreateBookingRadar,
							},
						];
					}

					return [
						{
							children: (
								<Flex direction="column">
									<Text fw={700} fz={18} lh={'24px'}>
										Нет свободных мест
									</Text>
									<Text fw={500} fz={14} lh={'20px'}>
										включите радар
									</Text>
								</Flex>
							),
							color: 'red',
							variant: 'light',
						},
					];
				}

				default:
					return [
						{
							children: 'Забронировать',
							...(currentForm().isValid()
								? {
										loading: isPending || isCreateMultipleBooking || isCreatingExternalBooking,
										onClick: handleCreateNewBooking,
									}
								: { variant: 'light', onClick: currentForm().validate }),
						},
					];
			}
		}

		return [
			{
				children: 'Забронировать',
				...(currentForm().isValid()
					? {
							loading: isPending || isCreateMultipleBooking || isCreatingExternalBooking,
							onClick: handleCreateNewBooking,
						}
					: { variant: 'light', onClick: currentForm().validate }),
			},
		];
	};

	useEffect(() => {
		radarForm.setValues({
			...radarForm.values,
			date: [
				(dayjs(date[0]).isToday() ? getNearestDate(TODAY) : dayjs(date[0])).toDate(),
				(dayjs(date[1]).isAfter(TODAY)
					? dayjs(date[1])
					: getNearestDate(TODAY).endOf('day')
				).toDate(),
			],
		});
	}, []);

	useEffect(() => {
		if (location && currentLocation) {
			handleResetStates();
			setBookingPlace(null);
			setBookingLocation(currentLocation);
			const names = location?.route?.map((item) => item?.name).join(' • ');

			setModel({
				...model,
				location: {
					label: names,
					id: bookingLocation?.id ?? currentLocation?.id,
				},
			});
		}
	}, [currentLocation, location]);

	useEffect(() => {
		if (bookingDate[0] && bookingDate[1]) {
			const formFromDate = dayjs(currentForm().getValues().date[0]);
			const formToDate = dayjs(currentForm().getValues().date[1]);

			const dateFrom = dayjs(bookingDate[0])
				.set('hour', formFromDate.hour())
				.set('minute', formFromDate.minute())
				.set('second', formFromDate.second());

			const dateTo = dayjs(bookingDate[1])
				.set('hour', formToDate.hour())
				.set('minute', formToDate.minute())
				.set('second', formToDate.second());

			currentForm().setFieldValue('date', [dateFrom.toDate(), dateTo.toDate()]);
			setBookingDate([null, null]);
		}
	}, [bookingDate]);

	useEffect(() => {
		currentForm().setFieldValue('booking_object_id', bookingPlace?.id);
		radarForm.setFieldValue('booking_object_id', bookingPlace?.id);
	}, [bookingPlace]);

	useEffect(() => {
		currentForm().setFieldValue('client_id', targetPersonId);
	}, [targetPersonId, type]);

	const content = (
		<Box pos="relative" pb={100}>
			<LoadingOverlay
				visible={
					isAvailableObjectsLoading ||
					isPending ||
					isCreateMultipleBooking ||
					isCreatingExternalBooking
				}
				zIndex={2}
				overlayProps={{ radius: 2, blur: 2 }}
			/>

			<Flex direction="column" gap={24}>
				<BookingTypeSlider
					initialType={currentForm().values?.kind}
					date={currentForm().values?.date}
					onChange={handleChangeBookingType}
					showLoad
				/>
				{currentBookingLoad !== null && currentBookingLoad >= 100 && (
					<BookingRadar
						isRadar={isRadar}
						setIsRadar={(isRadar) => {
							if (isRadar) {
								radarForm.setFieldValue('only_notify', isRadar ? false : null);
							} else {
								radarForm.reset();
							}

							setIsRadar(isRadar);
							handleSetFastBooking(isRadar);

							// Строго после handleSetFastBooking: он затирает pass_id устаревшими значениями
							if (isRadar && !isGuest && !isTaxi) {
								radarForm.setFieldValue('pass_id', parkingPlaceForm.getValues().pass_id ?? null);
							}
						}}
						setRadarTime={(time) =>
							radarForm.setFieldValue(
								'time_before_stop',
								time ? dayjs().startOf('day').add(time, 'minute').format('HH:mm:ss') : '',
							)
						}
					/>
				)}

				<DateTimePicker
					defaultTime={model?.initTime}
					disabledDates={{
						minDate: TODAY.toDate(),
						//TODO: ограничение пикера по рейтингу, закоменчено до вывода на прод
						// maxDate:
						// 	currentForm().values.kind === 'parking_place'
						// 		? TODAY.add(userRate?.maxParkingDays, 'days').toDate()
						// 		: TODAY.add(userRate?.maxDefaultDays, 'days').toDate(),
						maxDate:
							currentForm().values.kind === 'parking_place'
								? TODAY.add(14, 'days').toDate()
								: TODAY.add(30, 'days').toDate(),
					}}
					isTaxi={isTaxi}
					withTimeSlider={!isTaxi}
					value={currentForm().values.date}
					onChange={handleChangeDate}
				/>
				<Flex direction="column" gap={24}>
					<Flex direction="column" gap={16}>
						<Text fz={20} fw={800} lh="26px">
							Локация
						</Text>

						<LabelInput
							label="Офис и этаж"
							placeholder="Не выбрано"
							type="location"
							iconSize={24}
							onClick={openLocationSelector}
							defaultValue={
								bookingLocationPath.length
									? bookingLocationPath.map((path) => path).join(' • ')
									: model?.location?.label
							}
							multiple
							readOnly
						/>
					</Flex>

					{currentForm().values?.kind === 'parking_place' && taxiPoints?.length > 0 && (
						<SwitchSelector
							name="Приеду на такси"
							description="Бронирование места под такси"
							checked={isTaxi}
							onChange={handleSetIsTaxi}
						/>
					)}

					{!isTaxi && (
						<>
							{!bookingPlace?.attributes?.hasOwnProperty('external') && (
								<SwitchSelector
									name="Быстрое бронирование"
									description="Бронирование любого свободного места"
									checked={isFastBooking}
									onChange={handleSetFastBooking}
								/>
							)}

							{(currentForm().values?.kind === 'parking_place' ||
								currentForm().values?.kind === 'work_park' ||
								currentForm().values?.kind === 'work_place') &&
								(admin || moderator) && (
									<SwitchSelector
										name="Для гостя"
										description="Бронирование гостевого места"
										checked={isGuest}
										onChange={handleSetIsGuest}
									/>
								)}
						</>
					)}

					<Flex direction="column" gap={16}>
						<>
							{isTaxi ? (
								<CarInput
									onChange={(carNumber) => {
										(currentForm() as Forms['parking_place']).setFieldValue(
											'car_number',
											String(carNumber).toUpperCase(),
										);
									}}
								/>
							) : (
								<>
									<Flex gap={8}>
										<LabelInput
											label={bookingType?.label}
											{...(isFastBooking
												? {
														type: 'lock',
														placeholder: 'Любое',
														defaultValue: '',
													}
												: {
														type: currentForm().values?.kind,
														placeholder: 'Выбрать из списка',
														defaultValue: bookingPlace?.name.replace(/\s\|\s[\d\D]+/gm, '') ?? '',
														onClick: openPlaceSelector,
													})}
											readOnly
										/>
									</Flex>

									{currentForm().values.kind === 'meeting_room' && (
										<BookingMeetingRoomFields
											form={currentForm()}
											isAvailableToExternalMeeting={bookingPlace?.attributes?.hasOwnProperty(
												'external',
											)}
											isExternalMeeting={isExternalMeeting}
											isServiceRequired={isServiceRequired}
											isShowCarNumberField={
												(currentForm() as Forms['meeting_room']).getInputProps('show_auto').value
											}
											onExternalMeeting={handleSetIsExternalMeeting}
											onServiceRequired={handleSetIsServiceRequired}
											onShowCarNumberField={handleSetIsShowCarNumberField}
										/>
									)}

									{(currentForm().values?.kind === 'parking_place' ||
										currentForm().values?.kind === 'work_park') && (
										<>
											{isGuest ? (
												<>
													<GuestFields form={currentForm()} isParking />
													<CarInput
														onChange={(carNumber) => {
															(currentForm() as Forms['parking_place']).setFieldValue(
																'car_number',
																String(carNumber).toUpperCase(),
															);
															radarForm.setFieldValue('car_number', carNumber);
														}}
													/>
												</>
											) : (
												<CarPassSelector
													{...(currentForm() as Forms['parking_place']).getInputProps('pass_id')}
													carPasses={carPasses}
													onChange={(passId) => {
														(currentForm() as Forms['parking_place']).setValues({
															...currentForm().values,
															pass_id: Number(passId),
															car_number: carPasses.find((pass) => pass.value === passId)?.label,
														});

														radarForm.setFieldValue('pass_id', Number(passId));
													}}
												/>
											)}
										</>
									)}

									{currentForm().values?.kind === 'work_place' && isGuest && (
										<GuestFields form={currentForm()} isParking />
									)}
								</>
							)}
						</>
					</Flex>
				</Flex>
				{isMobile ? (
					<MapButton
						text="Выбрать место на карте"
						objects={isTaxi && taxiPoints?.length > 0 ? communalPlaces[0] : availableObjects}
						objectType={currentForm().values?.kind}
						disabled={isFastBooking}
						showOnMap={isTaxi}
						currentLocation={model?.location?.label}
						onSave={handleChangePlace}
						date={currentForm().values?.date}
					/>
				) : (
					<PlaceMap
						parent={isTaxi && taxiPoints?.length > 0 ? communalPlaces[0] : availableObjects}
						isSingle={isTaxi}
						objectType={currentForm().values?.kind}
						date={currentForm().values?.date}
					/>
				)}
			</Flex>

			<LocationSelectorSheet
				title="Локация"
				open={openedLocationSelector}
				close={closeLocationSelector}
				onChange={(location, path) => {
					console.log(location, path);
					setBookingLocation(location);
					setBookingLocationPath(path);
				}}
			/>

			{openedPlaceSelector && (
				<PlaceSelectorSheet
					isRadar={isRadar}
					type={currentForm().values?.kind}
					date={currentForm().values?.date}
					open={openedPlaceSelector}
					close={closePlaceSelector}
					onSave={handleChangePlace}
				/>
			)}
		</Box>
	);

	if (isMobile)
		return (
			<BottomSheet
				opened={open}
				onClose={() => {
					setBookingPlace(null);
					setBookingLocation(null);
					close?.();
				}}
				title="Бронирование"
				withCloseButton={false}
				buttons={actions()}
				withBackButton
				isFullScreen
			>
				{content}
			</BottomSheet>
		);

	return (
		<LayoutRow
			title={{
				title: { children: 'Бронирование', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)' }}
			buttons={actions()}
		>
			<Box style={{ overflowY: 'auto' }} h="100%" pr="md">
				{content}
			</Box>
		</LayoutRow>
	);
};
